import { useState, useEffect, useCallback } from 'react';
import { Box, Card, Button, Chip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import { usePaginationPreference } from 'hooks/usePaginationPreference';
import { useDialog } from 'common/DialogProvider';
import { HRApiWrapper } from 'api/HRApiWrapper';
import TrainingCourseDrawer from './TrainingCourseDrawer';
import TrainingCourseDetail from './TrainingCourseDetail';

const TrainingCoursesPage: React.FC = () => {
  const { employeeApi }: Api = useApi();
  const hrApi = new HRApiWrapper(employeeApi);
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();

  const [items, setItems] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [errors] = useState({});

  const reloadSelected = useCallback(async (id: number) => {
    try {
      const res = await hrApi.getTrainingCourseById('1', id);
      const fresh = res.data?.data ?? res.data;
      setSelected(fresh);
    } catch (error) {
      handleAPIError(error, toast, errors);
    }
  }, []);
  const [paginationModel, setPaginationModel] = usePaginationPreference('hrCourses', 25);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesRes, skillsRes] = await Promise.all([
        hrApi.getTrainingCourses('1', 1, 1000),
        hrApi.getSkills('1', 1, 1000),
      ]);
      setItems(coursesRes.data?.data?.queryResult ?? coursesRes.data?.data ?? []);
      setSkills(skillsRes.data?.data?.queryResult ?? skillsRes.data?.data ?? []);
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, []);

  const handleDelete = (id: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.roleRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          try {
            await hrApi.deleteTrainingCourse('1', id);
            fetchAll();
          } catch (error) { handleAPIError(error, toast, errors); }
          finally { closeDialog(dialogId); }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => closeDialog(dialogId),
      },
    });
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: intl.get('hr.course.name'), flex: 1 },
    { field: 'description', headerName: intl.get('hr.course.description'), flex: 1 },
    { field: 'duration', headerName: intl.get('hr.course.duration'), width: 120 },
    {
      field: 'price', headerName: intl.get('hr.course.price'), width: 120,
      valueFormatter: (value: any) => value != null ? `$${value}` : '',
    },
    {
      field: 'skills', headerName: intl.get('hr.course.skills'), width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', height: '100%' }}>
          {(params.row.trainingCourseSkills ?? []).slice(0, 3).map((s: any) => (
            <Chip key={s.id} label={s.skill?.name} size="small" />
          ))}
          {(params.row.trainingCourseSkills ?? []).length > 3 && (
            <Chip label={`+${(params.row.trainingCourseSkills ?? []).length - 3}`} size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      field: 'actions', headerName: '', width: 140, sortable: false,
      renderCell: (params) => (
        <DefaultActions row={params.row} entityType={ENTITY_TYPE.EMPLOYEE}
          onEdit={() => { reloadSelected(params.row.id); setDetailOpen(true); }}
          onDelete={() => handleDelete(params.row.id)}
          editScopes={['recruitment.manage']} deleteScopes={['recruitment.manage']} />
      ),
    },
  ];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 3 }}>
          {intl.get('mainNavigation.humanResources.trainingCourses')}
        </Typography>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDrawerOpen(true); }}>
            {intl.get('common.add')}
          </Button>
        </Box>
        {loading ? <Spinner /> : (
          <Box style={{ width: '100%', display: 'flex' }}>
            <DataGrid
              rows={items}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              sortingMode="client"
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Box>

      <TrainingCourseDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={async (data) => { await hrApi.createTrainingCourse('1', data); setDrawerOpen(false); fetchAll(); }}
        item={null}
      />

      {selected && (
        <TrainingCourseDetail
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          item={selected}
          skills={skills}
          onUpdate={async (data) => { await hrApi.updateTrainingCourse('1', selected.id, data); await reloadSelected(selected.id); fetchAll(); }}
          onAddSkill={async (idSkill) => { await hrApi.addSkillToTrainingCourse('1', selected.id, { idSkill }); await reloadSelected(selected.id); fetchAll(); }}
          onRemoveSkill={async (skillId) => { await hrApi.removeSkillFromTrainingCourse('1', selected.id, skillId); await reloadSelected(selected.id); fetchAll(); }}
        />
      )}
    </Card>
  );
};

export default TrainingCoursesPage;
